import { useEffect, useState, useCallback } from "react";
import {
  Briefcase, GraduationCap, Plus, MapPin, Sparkle, CalendarPlus, ShareNetwork, Trash, Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Shared";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CATEGORIES = ["logistics", "driving", "warehouse", "workshop", "production", "maintenance",
  "quality", "purchasing", "admin", "sales", "other"];
const TYPES = ["permanent", "contract", "casual", "apprenticeship", "traineeship", "internship"];
const PERIODS = ["hour", "day", "week", "month", "year"];
const MODES = ["classroom", "online", "onsite", "apprenticeship", "blended"];

const emptyJob = {
  title: "", category: "logistics", employment_type: "permanent", description: "",
  requirements: "", site: "", city: "", country: "US", pay_min: "", pay_max: "",
  currency: "usd", pay_period: "month", shift: "", positions: 1, closing_date: "",
  public: true, scheme_id: "",
};
const emptyProgram = {
  title: "", provider_name: "", category: "logistics", mode: "classroom", level: "",
  certification: "", duration_weeks: 4, intake_dates: "", cost: "", currency: "usd",
  funded: false, city: "", country: "US", international: false, description: "", seats: 20,
};

const money = (j) => {
  if (!j.pay_min && !j.pay_max) return "Pay on application";
  const cur = (j.currency || "usd").toUpperCase();
  const range = j.pay_max && j.pay_max !== j.pay_min
    ? `${Number(j.pay_min).toLocaleString()} – ${Number(j.pay_max).toLocaleString()}`
    : Number(j.pay_max || j.pay_min).toLocaleString();
  return `${cur} ${range} / ${j.pay_period}`;
};

export default function Jobs() {
  const { user } = useAuth();
  const [tab, setTab] = useState("board");
  const [jobs, setJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [enrolments, setEnrolments] = useState({ mine: [], as_provider: [] });
  const [schemes, setSchemes] = useState([]);
  const [profile, setProfile] = useState(null);
  const [profileDraft, setProfileDraft] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [jobForm, setJobForm] = useState(emptyJob);
  const [programForm, setProgramForm] = useState(emptyProgram);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [applyTo, setApplyTo] = useState(null);
  const [coverNote, setCoverNote] = useState("");
  const [inbox, setInbox] = useState({ job: null, rows: [] });
  const [ranking, setRanking] = useState("");
  const [interview, setInterview] = useState({ app: null, at: "", mode: "onsite", place_or_link: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const canPost = user?.role === "admin" || user?.role === "vendor";

  const load = useCallback(async () => {
    try {
      const [b, mine, apps, tr, en, pr] = await Promise.all([
        api.get(`/jobs?${filter !== "all" ? `category=${filter}&` : ""}${search ? `q=${encodeURIComponent(search)}` : ""}`),
        canPost ? api.get("/jobs?mine=true") : Promise.resolve({ data: [] }),
        api.get("/jobs/applications/mine"),
        api.get("/jobs/training"),
        api.get("/jobs/enrolments"),
        api.get("/jobs/profile"),
      ]);
      setJobs(b.data); setMyJobs(mine.data); setApplications(apps.data);
      setPrograms(tr.data); setEnrolments(en.data); setProfile(pr.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [canPost, filter, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canPost) return;
    api.get("/finance/schemes").then(({ data }) => setSchemes(data)).catch(() => {});
  }, [canPost]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const postJob = () => run(async () => {
    await api.post("/jobs", {
      ...jobForm,
      requirements: jobForm.requirements.split("\n").map((r) => r.trim()).filter(Boolean),
      pay_min: Number(jobForm.pay_min) || 0,
      pay_max: Number(jobForm.pay_max) || 0,
      positions: Number(jobForm.positions) || 1,
      closing_date: jobForm.closing_date || null,
      scheme_id: jobForm.scheme_id || null,
    });
    setDialog(null);
    setJobForm(emptyJob);
  }, "Vacancy posted");

  const draftAdvert = () => run(async () => {
    const { data } = await api.post("/jobs/ai/advert", {
      title: jobForm.title, category: jobForm.category,
      employment_type: jobForm.employment_type, notes: jobForm.description,
    });
    setJobForm((f) => ({ ...f, description: data.description }));
  }, "Advert drafted");

  const postProgram = () => run(async () => {
    await api.post("/jobs/training", {
      ...programForm,
      duration_weeks: Number(programForm.duration_weeks) || 1,
      seats: Number(programForm.seats) || 1,
      cost: Number(programForm.cost) || 0,
      intake_dates: programForm.intake_dates.split(",").map((d) => d.trim()).filter(Boolean),
    });
    setDialog(null);
    setProgramForm(emptyProgram);
  }, "Programme published");

  const openInbox = async (job) => {
    try {
      const { data } = await api.get(`/jobs/${job.id}/applications`);
      setInbox({ job, rows: data });
      setRanking("");
      setDialog("inbox");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const decide = async (app, status) => {
    const okDone = await run(() => api.patch(`/jobs/applications/${app.id}`, { status }), `Marked ${status}`);
    if (okDone && inbox.job) openInbox(inbox.job);
  };

  const rank = () => run(async () => {
    const { data } = await api.post(`/jobs/${inbox.job.id}/ai/match`);
    setRanking(data.ranking);
  });

  const saveInterview = () => run(async () => {
    await api.post(`/jobs/applications/${interview.app.id}/interviews`, {
      at: interview.at, mode: interview.mode,
      place_or_link: interview.place_or_link, notes: interview.notes,
    });
    setDialog(null);
    setInterview({ app: null, at: "", mode: "onsite", place_or_link: "", notes: "" });
  }, "Interview scheduled");

  const share = (job) => {
    navigator.clipboard?.writeText(`${window.location.origin}/jobs/${job.share_token}`).catch(() => {});
    toast.success("Vacancy link copied — post it anywhere");
  };

  const saveProfile = () => run(async () => {
    await api.put("/jobs/profile", {
      ...profileDraft,
      skills: typeof profileDraft.skills === "string" ? profileDraft.skills.split(",").map((s) => s.trim()).filter(Boolean) : profileDraft.skills,
      certifications: typeof profileDraft.certifications === "string" ? profileDraft.certifications.split(",").map((s) => s.trim()).filter(Boolean) : profileDraft.certifications,
      licences: typeof profileDraft.licences === "string" ? profileDraft.licences.split(",").map((s) => s.trim()).filter(Boolean) : profileDraft.licences,
      experience_years: Number(profileDraft.experience_years) || 0,
    });
    setDialog(null);
  }, "Profile saved");

  const TABS = [
    ["board", "Job board", Briefcase],
    ["applications", `My applications (${applications.length})`, Check],
    ...(canPost ? [["hiring", `Hiring (${myJobs.length})`, Briefcase]] : []),
    ["training", "Training & apprenticeships", GraduationCap],
    ["learning", `My learning (${enrolments.mine.length})`, GraduationCap],
  ];

  return (
    <div data-testid="jobs-page">
      <PageHeader
        overline="Work & skills"
        title="Jobs & training"
        description="Vacancies across the floor, the yard and the road — apply, get interviewed, and pick up a trade through vocational courses and apprenticeships."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" data-testid="edit-profile-button" onClick={() => { setProfileDraft({ ...profile }); setDialog("profile"); }}>
              My work profile
            </Button>
            {canPost && (
              <Button data-testid="post-job-button" onClick={() => { setJobForm(emptyJob); setDialog("job"); }}>
                <Plus size={16} className="mr-1.5" /> Post a vacancy
              </Button>
            )}
          </div>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          {TABS.map(([value, label, Icon]) => (
            <TabsTrigger key={value} value={value} data-testid={`jobs-tab-${value}`} className="whitespace-nowrap rounded-none text-xs uppercase tracking-wider">
              <Icon size={14} className="mr-1.5" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === "board" && (
        <div data-testid="board-panel">
          <div className="mb-4 grid gap-2 sm:grid-cols-[1fr_220px]">
            <Input data-testid="job-search-input" placeholder="Search vacancies…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="job-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Every trade</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {jobs.length === 0 ? (
            <EmptyState testid="no-jobs" title="No vacancies open" description={search || filter !== "all" ? "Nothing matches that search — clear the filters to see every vacancy." : "When an employer posts work it shows up here."} />
          ) : (
            <div className="grid gap-3 lg:grid-cols-2" data-testid="job-list">
              {jobs.map((j) => (
                <div key={j.id} data-testid={`job-${j.id}`} className="border border-border bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">{j.title}</div>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                        <span>{j.employer_name}</span>
                        <span className="inline-flex items-center gap-1"><MapPin size={12} />{[j.city, j.country].filter(Boolean).join(", ")}</span>
                        <span className="uppercase tracking-wider">{j.employment_type}</span>
                        <span className="uppercase tracking-wider">{j.category}</span>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-[#002FA7]">{money(j)}</span>
                  </div>
                  {j.description && <p className="mt-2 line-clamp-3 text-xs text-slate-600">{j.description}</p>}
                  {j.scheme_name && (
                    <p className="mt-2 border border-[#A7E3CE] bg-[#E6F7F1] px-2 py-1 text-[0.7rem] text-[#0B7A5B]">
                      Government support: {j.scheme_name}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      data-testid={`apply-${j.id}`}
                      disabled={j.applied || j.employer_id === user?.id}
                      onClick={() => { setApplyTo(j); setCoverNote(""); setDialog("apply"); }}
                    >
                      {j.applied ? "Applied" : "Apply"}
                    </Button>
                    {j.share_token && (
                      <Button size="sm" variant="outline" data-testid={`share-job-${j.id}`} onClick={() => share(j)}>
                        <ShareNetwork size={14} className="mr-1" /> Share
                      </Button>
                    )}
                    <span className="text-xs text-slate-400">{j.application_count} applied</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "applications" && (
        <div data-testid="applications-panel">
          {applications.length === 0 ? (
            <EmptyState testid="no-applications" title="You have not applied yet" description="Apply from the job board and track the outcome here." />
          ) : (
            <div className="space-y-3">
              {applications.map((a) => (
                <div key={a.id} data-testid={`application-${a.id}`} className="border border-border bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-[#0F172A]">{a.job_title}</div>
                      <div className="text-xs text-slate-500">{a.employer_name} · {a.reference}</div>
                    </div>
                    <StatusBadge value={a.status} testid={`application-status-${a.id}`} />
                  </div>
                  {(a.interviews || []).map((iv) => (
                    <div key={iv.id} data-testid={`interview-${iv.id}`} className="mt-2 border border-[#CBD9FF] bg-[#EEF3FF] px-3 py-2 text-xs text-[#002FA7]">
                      Interview {iv.at} · {iv.mode} · {iv.place_or_link || "details to follow"}
                      {iv.outcome ? ` · outcome: ${iv.outcome}` : ""}
                    </div>
                  ))}
                  {a.scheme_claim_ref && (
                    <p className="mt-2 text-xs text-[#0B7A5B]">Apprenticeship support claimed: {a.scheme_claim_ref}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "hiring" && canPost && (
        <div data-testid="hiring-panel">
          {myJobs.length === 0 ? (
            <EmptyState testid="no-my-jobs" title="You have not posted a vacancy" description="Post one and applications land here." />
          ) : (
            <div className="space-y-3">
              {myJobs.map((j) => (
                <div key={j.id} data-testid={`my-job-${j.id}`} className="border border-border bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-[#0F172A]">{j.title}</div>
                      <div className="text-xs text-slate-500">{j.reference} · {j.application_count} applied · {j.status}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" data-testid={`inbox-${j.id}`} onClick={() => openInbox(j)}>
                        Applications
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`close-job-${j.id}`} onClick={() => run(() => api.post(`/jobs/${j.id}/close`), "Updated")}>
                        {j.status === "open" ? "Close" : "Reopen"}
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`delete-job-${j.id}`} onClick={() => run(() => api.delete(`/jobs/${j.id}`), "Deleted")}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "training" && (
        <div data-testid="training-panel">
          {canPost && (
            <div className="mb-4 flex justify-end">
              <Button data-testid="post-program-button" onClick={() => { setProgramForm(emptyProgram); setDialog("program"); }}>
                <Plus size={16} className="mr-1.5" /> Publish a programme
              </Button>
            </div>
          )}
          {programs.length === 0 ? (
            <EmptyState testid="no-programs" title="No programmes yet" description="Vocational courses and apprenticeships will be listed here." />
          ) : (
            <div className="grid gap-3 lg:grid-cols-2" data-testid="program-list">
              {programs.map((p) => (
                <div key={p.id} data-testid={`program-${p.id}`} className="border border-border bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">{p.title}</div>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                        <span>{p.provider_name}</span>
                        <span className="uppercase tracking-wider">{p.mode}</span>
                        <span>{p.duration_weeks} weeks</span>
                        <span>{[p.city, p.country].filter(Boolean).join(", ")}</span>
                        {p.international && <span className="uppercase tracking-wider">international</span>}
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-[#002FA7]">
                      {p.funded || !Number(p.cost) ? "Free / funded" : `${(p.currency || "usd").toUpperCase()} ${Number(p.cost).toLocaleString()}`}
                    </span>
                  </div>
                  {p.certification && <p className="mt-2 text-xs text-slate-600">Leads to: {p.certification}{p.level ? ` (level ${p.level})` : ""}</p>}
                  {p.description && <p className="mt-2 line-clamp-3 text-xs text-slate-600">{p.description}</p>}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button size="sm" data-testid={`enrol-${p.id}`} onClick={() => run(() => api.post(`/jobs/training/${p.id}/enrol`, { intake_date: (p.intake_dates || [])[0] || null }), "Enrolled")}>
                      Enrol
                    </Button>
                    <span className="text-xs text-slate-400">{p.enrolled_count}/{p.seats} seats taken</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "learning" && (
        <div data-testid="learning-panel" className="space-y-6">
          <div>
            <div className="overline mb-2 text-slate-500">My courses</div>
            {enrolments.mine.length === 0 ? (
              <EmptyState testid="no-enrolments" title="Nothing enrolled yet" description="Pick a course or apprenticeship from the training tab." />
            ) : (
              <div className="space-y-3">
                {enrolments.mine.map((e) => (
                  <div key={e.id} data-testid={`enrolment-${e.id}`} className="border border-border bg-white p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-[#0F172A]">{e.program_title}</div>
                        <div className="text-xs text-slate-500">{e.provider_name} · {e.reference}{e.intake_date ? ` · starts ${e.intake_date}` : ""}</div>
                      </div>
                      <StatusBadge value={e.status} testid={`enrolment-status-${e.id}`} />
                    </div>
                    <div className="mt-2 h-1.5 w-full bg-slate-100">
                      <div className="h-1.5 bg-[#002FA7]" style={{ width: `${e.progress_pct || 0}%` }} />
                    </div>
                    {e.certificate_ref && <p className="mt-2 text-xs text-[#0B7A5B]">Certificate {e.certificate_ref}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
          {enrolments.as_provider.length > 0 && (
            <div>
              <div className="overline mb-2 text-slate-500">Learners on my programmes</div>
              <div className="space-y-3">
                {enrolments.as_provider.map((e) => (
                  <div key={e.id} data-testid={`learner-${e.id}`} className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-4">
                    <div>
                      <div className="text-sm font-semibold text-[#0F172A]">{e.learner_name}</div>
                      <div className="text-xs text-slate-500">{e.program_title} · {e.reference} · {e.progress_pct || 0}%</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" data-testid={`progress-${e.id}`} onClick={() => run(() => api.patch(`/jobs/enrolments/${e.id}`, { progress_pct: Math.min(100, (e.progress_pct || 0) + 25), status: "in_progress" }), "Progress saved")}>
                        +25%
                      </Button>
                      <Button size="sm" data-testid={`complete-${e.id}`} onClick={() => run(() => api.patch(`/jobs/enrolments/${e.id}`, { status: "completed" }), "Marked complete")}>
                        Complete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Post a vacancy */}
      <Dialog open={dialog === "job"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Post a vacancy</DialogTitle>
            <DialogDescription>Anyone signed in can apply. A public vacancy also gets a shareable link.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Job title</Label>
              <Input data-testid="job-title-input" value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} placeholder="Warehouse forklift operator" />
            </div>
            <div>
              <Label>Trade</Label>
              <Select value={jobForm.category} onValueChange={(v) => setJobForm({ ...jobForm, category: v })}>
                <SelectTrigger data-testid="job-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Employment type</Label>
              <Select value={jobForm.employment_type} onValueChange={(v) => setJobForm({ ...jobForm, employment_type: v, scheme_id: "" })}>
                <SelectTrigger data-testid="job-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>City</Label><Input data-testid="job-city-input" value={jobForm.city} onChange={(e) => setJobForm({ ...jobForm, city: e.target.value })} /></div>
            <div><Label>Country</Label><Input data-testid="job-country-input" value={jobForm.country} onChange={(e) => setJobForm({ ...jobForm, country: e.target.value })} placeholder="US" /></div>
            <div><Label>Pay from</Label><Input data-testid="job-pay-min-input" type="number" value={jobForm.pay_min} onChange={(e) => setJobForm({ ...jobForm, pay_min: e.target.value })} /></div>
            <div><Label>Pay to</Label><Input data-testid="job-pay-max-input" type="number" value={jobForm.pay_max} onChange={(e) => setJobForm({ ...jobForm, pay_max: e.target.value })} /></div>
            <div>
              <Label>Per</Label>
              <Select value={jobForm.pay_period} onValueChange={(v) => setJobForm({ ...jobForm, pay_period: v })}>
                <SelectTrigger data-testid="job-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PERIODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Positions</Label><Input data-testid="job-positions-input" type="number" value={jobForm.positions} onChange={(e) => setJobForm({ ...jobForm, positions: e.target.value })} /></div>
            <div><Label>Shift</Label><Input data-testid="job-shift-input" value={jobForm.shift} onChange={(e) => setJobForm({ ...jobForm, shift: e.target.value })} placeholder="Days, 06:00–14:00" /></div>
            <div><Label>Closing date</Label><Input data-testid="job-closing-input" type="date" value={jobForm.closing_date} onChange={(e) => setJobForm({ ...jobForm, closing_date: e.target.value })} /></div>
            {["apprenticeship", "traineeship"].includes(jobForm.employment_type) && (
              <div className="sm:col-span-2">
                <Label>Government scheme (optional)</Label>
                <Select value={jobForm.scheme_id || "none"} onValueChange={(v) => setJobForm({ ...jobForm, scheme_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="job-scheme-select"><SelectValue placeholder="No scheme" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No scheme</SelectItem>
                    {schemes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between">
                <Label>Advert</Label>
                <Button size="sm" variant="outline" data-testid="ai-advert-button" disabled={busy || !jobForm.title} onClick={draftAdvert}>
                  <Sparkle size={14} className="mr-1" /> Write it for me
                </Button>
              </div>
              <Textarea data-testid="job-description-input" rows={5} value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>What you need (one per line)</Label>
              <Textarea data-testid="job-requirements-input" rows={3} value={jobForm.requirements} onChange={(e) => setJobForm({ ...jobForm, requirements: e.target.value })} placeholder={"Forklift licence\n2 years in a warehouse"} />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600 sm:col-span-2">
              <input type="checkbox" data-testid="job-public-checkbox" checked={jobForm.public} onChange={(e) => setJobForm({ ...jobForm, public: e.target.checked })} />
              Publish publicly with a shareable link
            </label>
          </div>
          <DialogFooter>
            <Button data-testid="submit-job-button" onClick={postJob} disabled={busy || !jobForm.title}>Post vacancy</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply */}
      <Dialog open={dialog === "apply"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply for {applyTo?.title}</DialogTitle>
            <DialogDescription>Your work profile is sent with the application.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="cover-note-input" rows={4} value={coverNote} onChange={(e) => setCoverNote(e.target.value)} placeholder="Why you are a good fit" />
          <DialogFooter>
            <Button data-testid="submit-application-button" disabled={busy} onClick={() => run(async () => {
              await api.post(`/jobs/${applyTo.id}/applications`, { cover_note: coverNote });
              setDialog(null);
            }, "Application sent")}>
              Send application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employer inbox */}
      <Dialog open={dialog === "inbox"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{inbox.job?.title} — applications</DialogTitle>
            <DialogDescription>Shortlist, book an interview and record the outcome.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button size="sm" variant="outline" data-testid="ai-match-button" disabled={busy} onClick={rank}>
              <Sparkle size={14} className="mr-1" /> Rank candidates
            </Button>
          </div>
          {ranking && (
            <div data-testid="ai-ranking" className="whitespace-pre-line border border-[#CBD9FF] bg-[#EEF3FF] p-3 text-xs text-[#0F172A]">
              {ranking}
            </div>
          )}
          <div className="space-y-3">
            {inbox.rows.length === 0 && <p className="text-sm text-slate-500">No applications yet.</p>}
            {inbox.rows.map((a) => (
              <div key={a.id} data-testid={`inbox-row-${a.id}`} className="border border-border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Avatar imageId={a.applicant_avatar_id} name={a.applicant_name} size="md" testid={`applicant-avatar-${a.id}`} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">{a.applicant_name}</div>
                      <div className="text-xs text-slate-500">
                        {a.reference} · {a.experience_years || 0} yrs · {(a.skills || []).join(", ") || "no skills listed"}
                      </div>
                    </div>
                  </div>
                  <StatusBadge value={a.status} testid={`inbox-status-${a.id}`} />
                </div>
                {a.cover_note && <p className="mt-2 text-xs text-slate-600">{a.cover_note}</p>}
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" data-testid={`shortlist-${a.id}`} onClick={() => decide(a, "shortlisted")}>Shortlist</Button>
                  <Button size="sm" variant="outline" data-testid={`interview-${a.id}`} onClick={() => { setInterview({ app: a, at: "", mode: "onsite", place_or_link: "", notes: "" }); setDialog("interview"); }}>
                    <CalendarPlus size={14} className="mr-1" /> Interview
                  </Button>
                  <Button size="sm" data-testid={`hire-${a.id}`} onClick={() => decide(a, "hired")}>Hire</Button>
                  <Button size="sm" variant="ghost" data-testid={`decline-${a.id}`} onClick={() => decide(a, "declined")}>Decline</Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Interview */}
      <Dialog open={dialog === "interview"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Book an interview</DialogTitle>
            <DialogDescription>{interview.app?.applicant_name}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div><Label>Date and time</Label><Input data-testid="interview-at-input" type="datetime-local" value={interview.at} onChange={(e) => setInterview({ ...interview, at: e.target.value })} /></div>
            <div>
              <Label>How</Label>
              <Select value={interview.mode} onValueChange={(v) => setInterview({ ...interview, mode: v })}>
                <SelectTrigger data-testid="interview-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="onsite">On site</SelectItem>
                  <SelectItem value="video">Video call</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Place or link</Label><Input data-testid="interview-place-input" value={interview.place_or_link} onChange={(e) => setInterview({ ...interview, place_or_link: e.target.value })} placeholder="Gate 2 reception, or a meeting link" /></div>
            <div><Label>Notes</Label><Textarea data-testid="interview-notes-input" rows={2} value={interview.notes} onChange={(e) => setInterview({ ...interview, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-interview-button" disabled={busy || !interview.at} onClick={saveInterview}>Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Programme */}
      <Dialog open={dialog === "program"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Publish a training programme</DialogTitle>
            <DialogDescription>Vocational courses, apprenticeships and short skills programmes.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Title</Label><Input data-testid="program-title-input" value={programForm.title} onChange={(e) => setProgramForm({ ...programForm, title: e.target.value })} /></div>
            <div><Label>Provider</Label><Input data-testid="program-provider-input" value={programForm.provider_name} onChange={(e) => setProgramForm({ ...programForm, provider_name: e.target.value })} /></div>
            <div>
              <Label>Trade</Label>
              <Select value={programForm.category} onValueChange={(v) => setProgramForm({ ...programForm, category: v })}>
                <SelectTrigger data-testid="program-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={programForm.mode} onValueChange={(v) => setProgramForm({ ...programForm, mode: v })}>
                <SelectTrigger data-testid="program-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Weeks</Label><Input data-testid="program-duration-input" type="number" value={programForm.duration_weeks} onChange={(e) => setProgramForm({ ...programForm, duration_weeks: e.target.value })} /></div>
            <div><Label>Certification</Label><Input data-testid="program-cert-input" value={programForm.certification} onChange={(e) => setProgramForm({ ...programForm, certification: e.target.value })} placeholder="Forklift operator licence" /></div>
            <div><Label>Level</Label><Input data-testid="program-level-input" value={programForm.level} onChange={(e) => setProgramForm({ ...programForm, level: e.target.value })} /></div>
            <div><Label>Cost</Label><Input data-testid="program-cost-input" type="number" value={programForm.cost} onChange={(e) => setProgramForm({ ...programForm, cost: e.target.value })} /></div>
            <div><Label>Seats</Label><Input data-testid="program-seats-input" type="number" value={programForm.seats} onChange={(e) => setProgramForm({ ...programForm, seats: e.target.value })} /></div>
            <div><Label>City</Label><Input data-testid="program-city-input" value={programForm.city} onChange={(e) => setProgramForm({ ...programForm, city: e.target.value })} /></div>
            <div><Label>Country</Label><Input data-testid="program-country-input" value={programForm.country} onChange={(e) => setProgramForm({ ...programForm, country: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Intake dates (comma separated)</Label><Input data-testid="program-intakes-input" value={programForm.intake_dates} onChange={(e) => setProgramForm({ ...programForm, intake_dates: e.target.value })} placeholder="2026-07-01, 2026-09-01" /></div>
            <div className="sm:col-span-2"><Label>About the programme</Label><Textarea data-testid="program-description-input" rows={4} value={programForm.description} onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="program-funded-checkbox" checked={programForm.funded} onChange={(e) => setProgramForm({ ...programForm, funded: e.target.checked })} />
              Funded or free
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="program-international-checkbox" checked={programForm.international} onChange={(e) => setProgramForm({ ...programForm, international: e.target.checked })} />
              Open to international learners
            </label>
          </div>
          <DialogFooter>
            <Button data-testid="submit-program-button" disabled={busy || !programForm.title} onClick={postProgram}>Publish</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Work profile */}
      <Dialog open={dialog === "profile"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>My work profile</DialogTitle>
            <DialogDescription>Sent with every application you make.</DialogDescription>
          </DialogHeader>
          {profileDraft && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label>Headline</Label><Input data-testid="profile-headline-input" value={profileDraft.headline || ""} onChange={(e) => setProfileDraft({ ...profileDraft, headline: e.target.value })} placeholder="Forklift driver, 6 years in cold storage" /></div>
              <div><Label>Years of experience</Label><Input data-testid="profile-experience-input" type="number" value={profileDraft.experience_years || 0} onChange={(e) => setProfileDraft({ ...profileDraft, experience_years: e.target.value })} /></div>
              <div><Label>City</Label><Input data-testid="profile-city-input" value={profileDraft.city || ""} onChange={(e) => setProfileDraft({ ...profileDraft, city: e.target.value })} /></div>
              <div><Label>Country</Label><Input data-testid="profile-country-input" value={profileDraft.country || "US"} onChange={(e) => setProfileDraft({ ...profileDraft, country: e.target.value })} /></div>
              <div><Label>Phone</Label><Input data-testid="profile-phone-input" value={profileDraft.phone || ""} onChange={(e) => setProfileDraft({ ...profileDraft, phone: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Skills (comma separated)</Label><Input data-testid="profile-skills-input" value={Array.isArray(profileDraft.skills) ? profileDraft.skills.join(", ") : profileDraft.skills || ""} onChange={(e) => setProfileDraft({ ...profileDraft, skills: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Certificates (comma separated)</Label><Input data-testid="profile-certs-input" value={Array.isArray(profileDraft.certifications) ? profileDraft.certifications.join(", ") : profileDraft.certifications || ""} onChange={(e) => setProfileDraft({ ...profileDraft, certifications: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Licences (comma separated)</Label><Input data-testid="profile-licences-input" value={Array.isArray(profileDraft.licences) ? profileDraft.licences.join(", ") : profileDraft.licences || ""} onChange={(e) => setProfileDraft({ ...profileDraft, licences: e.target.value })} /></div>
              <div><Label>Available from</Label><Input data-testid="profile-available-input" type="date" value={profileDraft.available_from || ""} onChange={(e) => setProfileDraft({ ...profileDraft, available_from: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-profile-button" disabled={busy} onClick={saveProfile}>Save profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
